// 🔧 ai_mission_control.js
// ✅ Purpose: Admin-facing AI Mission Control page for Let's Go Vote Sales Automation
// ✅ Features:
// - Shows current AI sales agent pipeline health
// - Displays Collection Manager, Discovery, Research, Outreach, and Follow-up agent cards
// - Loads recent Collection Manager jobs
// - Shows current prospect queue counts
// - Links to Sales Dashboard, Prospect Sources, Outreach Queue, and Prospects CRM
// - Does NOT send emails or contact prospects
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

function AiMissionControl() {
  const [prospects, setProspects] = useState([]);
  const [collectionJobs, setCollectionJobs] = useState([]);
  const [agentRuns, setAgentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [runningCollectionManager, setRunningCollectionManager] = useState(false);

  const showStatus = (type, message) => {
    setStatus({ type, message });

    setTimeout(() => {
      setStatus(null);
    }, 20000);
  };

  // ✅ Runs Collection Manager from AI Mission Control
  // ✅ Purpose: Lets admins operate the AI sales workflow from the agent operations page
  const handleRunCollectionManager = async () => {
    try {
      setRunningCollectionManager(true);

      showStatus(
        "info",
        "Collection Manager Agent is running."
      );

      const functions = getFunctions();
      const runCollectionManager = httpsCallable(
        functions,
        "runCollectionManager"
      );

      const result = await runCollectionManager({
        researchLimit: 3,
        draftLimit: 5,
      });

      console.log("✅ Collection Manager result:", result.data);

      await loadMissionControl();

      showStatus(
        "success",
        `Collection Manager complete. Research queued: ${result.data.researchQueued || 0}. Drafts queued: ${result.data.draftQueued || 0}. Ready for review: ${result.data.readyForReview || 0}.`
      );
    } catch (error) {
      console.error("❌ Collection Manager failed:", error);

      showStatus(
        "error",
        "Collection Manager failed. Check console for details."
      );
    } finally {
      setRunningCollectionManager(false);
    }
  };

  const loadMissionControl = async () => {
    try {
      setLoading(true);

      console.log("🤖 Loading AI Mission Control...");

      const prospectsSnap = await getDocs(
        query(collection(db, "prospects"), orderBy("createdAt", "desc"))
      );

      const loadedProspects = prospectsSnap.docs.map((prospectDoc) => ({
        id: prospectDoc.id,
        ...prospectDoc.data(),
      }));

      setProspects(loadedProspects);

      const jobsSnap = await getDocs(
        query(
          collection(db, "collection_manager_jobs"),
          orderBy("createdAt", "desc"),
          limit(10)
        )
      );

      const agentRunsSnap = await getDocs(
        query(
          collection(db, "agent_runs"),
          orderBy("createdAt", "desc"),
          limit(20)
        )
      );

      const loadedAgentRuns = agentRunsSnap.docs.map((runDoc) => ({
        id: runDoc.id,
        ...runDoc.data(),
      }));

      setAgentRuns(loadedAgentRuns);

      const loadedJobs = jobsSnap.docs.map((jobDoc) => ({
        id: jobDoc.id,
        ...jobDoc.data(),
      }));

      setCollectionJobs(loadedJobs);
    } catch (error) {
      console.error("❌ Failed to load AI Mission Control:", error);
      showStatus("error", "Failed to load AI Mission Control.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMissionControl();
  }, []);

  const counts = useMemo(() => {
    const needsResearch = prospects.filter(
      (p) =>
        p.collectionStage === "needs_research" ||
        !p.researchStatus ||
        p.researchStatus === "not_started" ||
        p.researchStatus === "failed"
    ).length;

    const needsDraft = prospects.filter(
      (p) =>
        p.collectionStage === "needs_draft" ||
        (p.researchStatus === "completed" &&
          !p.outreachDraft &&
          !p.emailOutreachDraft)
    ).length;

    const readyForReview = prospects.filter(
      (p) => p.outreachQueueStatus === "ready_for_review"
    ).length;

    const contacted = prospects.filter(
      (p) => p.status === "contacted" || p.outreachStatus === "sent"
    ).length;

    const highPriority = prospects.filter(
      (p) => Number(p.fitScore || 0) >= 8
    ).length;

    return {
      total: prospects.length,
      needsResearch,
      needsDraft,
      readyForReview,
      contacted,
      highPriority,
    };
  }, [prospects]);

  const lastJob = collectionJobs[0] || null;

  const getLatestAgentRun = (agentName) => {
    return agentRuns.find((run) => run.agentName === agentName) || null;
  };

  const latestCollectionRun = getLatestAgentRun("collection_manager");

return (
    <SalesPageLayout
      title="AI Mission Control"
      subtitle="Operations center for LGV Sales Automation agents and queues."
      status={status}
      actions={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRunCollectionManager}
            disabled={runningCollectionManager}
          >
            {runningCollectionManager
              ? "Running Collection Manager..."
              : "Run Collection Manager"}
          </button>

          <Link
            className="btn btn-outline-primary"
            to="/admin/sales-dashboard"
          >
            Sales Dashboard
          </Link>

          <Link
            className="btn btn-outline-secondary"
            to="/admin/prospect-sources"
          >
            Prospect Sources
          </Link>

          <Link
            className="btn btn-outline-secondary"
            to="/admin/outreach-queue"
          >
            Outreach Queue
          </Link>

          <Link
            className="btn btn-outline-dark"
            to="/admin/agent-runs"
          >
            Agent Runs
          </Link>
          <Link
            className="btn btn-outline-secondary"
            to="/admin/automation-scheduler"
          >
            Automation Scheduler
          </Link>
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={loadMissionControl}
          >
            Refresh
          </button>
        </>
      }
    >
    {loading ? (
    <div className="alert alert-info">Loading AI Mission Control...</div>
      ) : (
        <>
          <div className="row mb-4">
            <MetricCard title="Total Prospects" value={counts.total} />
            <MetricCard title="High Priority" value={counts.highPriority} />
            <MetricCard title="Needs Research" value={counts.needsResearch} />
            <MetricCard title="Needs Draft" value={counts.needsDraft} />
            <MetricCard title="Ready for Review" value={counts.readyForReview} />
            <MetricCard title="Contacted" value={counts.contacted} />
          </div>

          <div className="row mb-4">
            <AgentCard
              name="Collection Manager"
              status={latestCollectionRun?.status || "Ready"}
              description="Orchestrates prospect priority, research queue, draft queue, and review queue."
              link="/admin/sales-dashboard"
              linkLabel="Open Sales Dashboard"
            />

            <AgentCard
              name="Discovery Agent"
              status="Ready"
              description="Finds new prospects through search and saved source scanning."
              link="/admin/prospect-sources"
              linkLabel="Open Sources"
            />

            <AgentCard
              name="Research Agent"
              status="Ready"
              description="Enriches prospects with website, contact, social, voting, and sales intelligence."
              link="/admin/sales-dashboard"
              linkLabel="Open Research Queue"
            />

            <AgentCard
              name="Outreach Agent"
              status="Ready"
              description="Creates human-reviewable email and future social outreach drafts."
              link="/admin/outreach-queue"
              linkLabel="Open Outreach Queue"
            />

            <AgentCard
              name="Follow-up Agent"
              status="Coming Soon"
              description="Will prepare follow-up drafts when prospects are due for another touch."
              link="/admin/sales-dashboard"
              linkLabel="View Follow-ups"
            />
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Last Collection Manager Run</strong>
            </div>

            <div className="card-body">
              {!lastJob ? (
                <div className="alert alert-info mb-0">
                  No Collection Manager runs have been logged yet.
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-3 mb-2">
                    <strong>Status:</strong> {lastJob.status || "unknown"}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Total Prospects:</strong> {lastJob.totalProspects || 0}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Research Queued:</strong> {lastJob.researchQueued || 0}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Drafts Queued:</strong> {lastJob.draftQueued || 0}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Ready for Review:</strong> {lastJob.readyForReview || 0}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Research Limit:</strong> {lastJob.researchLimit || 0}
                  </div>

                  <div className="col-md-3 mb-2">
                    <strong>Draft Limit:</strong> {lastJob.draftLimit || 0}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Recent Agent Runs</strong>
            </div>

            <div className="card-body">
              {agentRuns.length === 0 ? (
                <div className="alert alert-info mb-0">
                  No standardized agent runs have been logged yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Status</th>
                        <th>Summary</th>
                        <th>Metrics</th>
                      </tr>
                    </thead>

                    <tbody>
                      {agentRuns.map((run) => (
                        <tr key={run.id}>
                          <td>
                            <strong>{run.agentName}</strong>
                            <div className="small text-muted">
                              {run.initiatedBy || "manual"}
                            </div>
                          </td>

                          <td>
                            <span className="badge bg-secondary">
                              {run.status || "unknown"}
                            </span>
                          </td>

                          <td>{run.summary || ""}</td>

                          <td>
                            <pre className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                              {JSON.stringify(run.metrics || {}, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <strong>Recent Collection Manager Jobs</strong>
            </div>

            <div className="card-body">
              {collectionJobs.length === 0 ? (
                <div className="text-muted">No jobs found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Total</th>
                        <th>Research Queued</th>
                        <th>Drafts Queued</th>
                        <th>Ready Review</th>
                      </tr>
                    </thead>

                    <tbody>
                      {collectionJobs.map((job) => (
                        <tr key={job.id}>
                          <td>
                            <span className="badge bg-secondary">
                              {job.status || "unknown"}
                            </span>
                          </td>
                          <td>{job.totalProspects || 0}</td>
                          <td>{job.researchQueued || 0}</td>
                          <td>{job.draftQueued || 0}</td>
                          <td>{job.readyForReview || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
  </SalesPageLayout>
  );
}

function MetricCard({ title, value }) {
  return (
    <div className="col-md-2 col-sm-6 mb-2">
      <div className="card text-center h-100">
        <div className="card-body">
          <h3>{value}</h3>
          <div className="small text-muted">{title}</div>
        </div>
      </div>
    </div>
  );
}

function AgentCard({ name, status, description, link, linkLabel }) {
  const badgeClass = status === "Coming Soon" ? "bg-secondary" : "bg-success";

  return (
    <div className="col-md-6 col-lg-4 mb-3">
      <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{name}</strong>
          <span className={`badge ${badgeClass}`}>{status}</span>
        </div>

        <div className="card-body">
          <p className="text-muted">{description}</p>

          <Link className="btn btn-outline-primary btn-sm" to={link}>
            {linkLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AiMissionControl;
